import type { ScreenManager } from "./screen-manager";

/** Buttons of the W3C standard gamepad layout used for menus. */
const A = 0;
const B = 1;
const START = 9;
const UP = 12;
const DOWN = 13;
const STICK_THRESHOLD = 0.5;
const REPEAT_DELAY_MS = 350;
const REPEAT_INTERVAL_MS = 120;

export type PadAction = "up" | "down" | "confirm" | "back" | "start";

interface PadLike {
	buttons: readonly { pressed: boolean }[];
	axes: readonly number[];
}

/** Which menu actions a pad is holding right now (d-pad or left stick for up/down). */
export function padActions(pad: PadLike): Set<PadAction> {
	const held = new Set<PadAction>();
	const btn = (i: number) => pad.buttons[i]?.pressed === true;
	const y = pad.axes[1] ?? 0;
	if (btn(UP) || y < -STICK_THRESHOLD) held.add("up");
	if (btn(DOWN) || y > STICK_THRESHOLD) held.add("down");
	if (btn(A)) held.add("confirm");
	if (btn(B)) held.add("back");
	if (btn(START)) held.add("start");
	return held;
}

/**
 * Menu navigation with any connected gamepad: d-pad or stick moves focus (with key-repeat),
 * A confirms, B goes back, Start pauses a running match or goes back in menus. Only acts
 * on button *presses*, so holding A to thrust doesn't click through menus.
 */
export class GamepadMenuNav {
	private previous = new Set<PadAction>();
	private repeatAt = 0;
	private frame = 0;

	constructor(private readonly ui: ScreenManager) {
		this.frame = requestAnimationFrame(this.poll);
	}

	destroy(): void {
		cancelAnimationFrame(this.frame);
	}

	private readonly poll = (now: number): void => {
		this.frame = requestAnimationFrame(this.poll);
		const pads = navigator.getGamepads?.() ?? [];
		const held = new Set<PadAction>();
		for (const pad of pads) {
			if (pad?.connected) for (const action of padActions(pad)) held.add(action);
		}
		const pressed = (a: PadAction) => held.has(a) && !this.previous.has(a);

		const menuOpen = !this.ui.isEmpty;
		if (menuOpen) {
			for (const dir of ["up", "down"] as const) {
				if (pressed(dir)) {
					this.ui.navigate(dir === "down" ? 1 : -1);
					this.repeatAt = now + REPEAT_DELAY_MS;
				} else if (held.has(dir) && now >= this.repeatAt) {
					this.ui.navigate(dir === "down" ? 1 : -1);
					this.repeatAt = now + REPEAT_INTERVAL_MS;
				}
			}
			if (pressed("confirm")) this.ui.confirm();
			if (pressed("back")) this.ui.back();
		}
		if (pressed("start")) this.ui.back();
		this.previous = held;
	};
}
